import { Observable } from 'rxjs/Rx';
import { Subject } from 'rxjs/Subject';
import { ReplaySubject } from 'rxjs/ReplaySubject';

// Classes.
import { LoggedComponent } from '../logger/logged-component';
import { DictList } from '../util/dict-list';
import { DataContainer, Metadata } from '../db/data-container';
import { DataContainerErroneous } from '../db/data-container-erroneous';
import { ResponseData } from '../fetching/wa-response/wa-api-response';

// Providers.
import { LoggerService, LogLevel } from '../logger/logger-service';
import { DatabaseService } from '../wa-services/database-service';
import { RequestedItems } from '../wa-services/param-builder-service';

// Interfaces
import { LocatorInterface } from '../interfaces/locator.interface';
import { ViewDataInterface, ViewDataConfigStruct } from '../interfaces/view-data.interface';



export abstract class ViewData<T> extends LoggedComponent implements ViewDataInterface {

  // Variable to store current DocumentContainer
  protected _currentDocument: DataContainer<T>;

  // Last time data was updated.
  protected _updatedTimestamp: number;

  // Number of items this view wants to have in memory. If different from
  // available items new data should be requested.
  protected _expectedItems: RequestedItems = 0;

  // If true, autorefresh is enabled.
  protected _autorefresh: boolean = true;

  // In miliseconds.
  protected _resfreshInterval: number = 1000;

  // Observable (Subject) with manual refresh requests
  protected manualRefreshStream: Subject<any>;

  // Internal stream to trigger refresh given the autorefresh interval.
  protected refreshTick: Observable<number>;


  protected dataStream: ReplaySubject<T[]>;
  protected dataAvailableStream: ReplaySubject<boolean>;
  protected dependentViewDataAvailable: ReplaySubject<boolean>;

  //protected publicDataHotStream: Observable<any>;

  // ViewData from which this ViewData depends on.
  protected dependentViewData: DictList<ViewDataInterface>;


  protected _maxAge: number;

  protected _metadata: Metadata;

  public constructor(
    protected logger: LoggerService,
    protected _db: DatabaseService,
    protected _locator: LocatorInterface) {
    super(logger);

    this.setLogLevel(LogLevel.LOG);
    this.applyConfiguration(new ViewDataConfigStruct);

    this.manualRefreshStream = new Subject<any>();
    this.dataStream = new ReplaySubject<T[]>(1);
    this.dataAvailableStream = new ReplaySubject<boolean>(1);
    this.dependentViewDataAvailable = new ReplaySubject<boolean>(1);
    this.dependentViewData = new DictList<ViewDataInterface>();
  }

  public applyConfiguration(confToApply: ViewDataConfigStruct) {
    let conf = new ViewDataConfigStruct();
    Object.assign(conf, confToApply);
    this._maxAge = conf.maxAge;
    this._resfreshInterval = conf.interval;
    this.autorefresh = conf.autorefresh;
    this._expectedItems = conf.expectedItems;
  }

  public init() {
    this.log(`Initalitzing view «${this.locator.getId()}»`);

    this.dependentViewDataAvailable
      .asObservable()
      .combineLatest(this.getDataFromDb())
      .map(combined => combined[1])
      .map((dataContainer: DataContainer<T>) => this.mapData(dataContainer))
      .do(dataContainer => this.nextDataContainer(dataContainer))
      .subscribe(() => this.streamSetup());
  }

  protected nextDataContainer(dataContainer: DataContainer<T>) {
    // Only get this doc if is not empty.
    if (!dataContainer.empty) {
      this._currentDocument = dataContainer;
      this.debug(`View «${this.locator.getId()}» recevied new data that it's available to subscribers:`, this._currentDocument);
      this.dataStream.next(dataContainer.getPayload())
      this.checkExpectedtemsFromTotalItems();

      this.dataAvailableStream.next(true);
    }
  }

  protected streamSetup() {

    this.refreshTick = Observable.interval(this._resfreshInterval)
      .filter(() => this._autorefresh)
      .do(() => this.debug(`Received tick to refresh view «${this.locator.getId()}»`))
      // Do not ask for data if it's not expired
      .filter(() => this.streamComponentCheckDocExpired() || !this.documentIsFresh());

    Observable.merge(
      this.refreshTick,
      this.manualRefreshStream.asObservable()
    ).do(a =>a)
      // Wait until dependent views have data.
      .combineLatest(this.dependentViewDataAvailable)
      .map(combined => combined[0])

      // Ask for fresh data to server.
      .flatMap(() => this.updateData())

      .do(() => this.log(`Requesting fresh data for view «${this.locator.getId()}» to server`))
      // Store received data in DB and get a Data Container.
      .flatMap(data => this.streamComponentStoreReceivedData(data))
      // Map items to internal types
      .map((dataContainer: DataContainer<T>) => this.mapData(dataContainer))
      .subscribe(updatedData => this.nextDataContainer(updatedData));

    // Initial request if data is not already present (loaded from db).
    if (!this.documentIsFresh()) {
      this.refresh();
    }
  }

  public deleteContent() {
    return this._db.truncate();
  }
  protected streamComponentStoreReceivedData(response: ResponseData): Observable<DataContainer<T>> {
    if (!response.isErroneous()) {
      this._metadata = new Metadata(response.getPageInfo().getTotalResults());
      let dataContainer = new DataContainer<T>(this.locator, response.getRawItems(), this._metadata, this._maxAge);
      return this._db.store(dataContainer);
    }
    else {
      return Observable.of(new DataContainerErroneous(this._locator));
    }
  }

  protected allExpectedItemsAreAvailable(): boolean {
    let rc: boolean = false;
    if (this._currentDocument) {
      if (this._expectedItems === 'ALL') {
        rc = this.availableItems === this._currentDocument.totalItems;
      }
      else {
        let maxAvailableItems = Math.min(this._expectedItems, this._currentDocument.totalItems);
        return this._currentDocument.availableItems >= maxAvailableItems;
      }
    }
    return rc;
  }

  protected streamComponentCheckDocExpired() {
    let rc: boolean = true;

    if (this._currentDocument
      && !this._currentDocument.expired
      && this.allExpectedItemsAreAvailable()) {
      this.debug(`View «${this.locator.getId()}» is not expired and has the expected number of items available, cancel refresh`);
      rc = false;
    }
    else {
      this.debug(`View «${this.locator.getId()}» is expired or empty, go on with the refresh`);
    }
    return rc;
  }

  public getDataStream(): Observable<any> {
    return this.dataStream.asObservable();
  }

  public get locator(): LocatorInterface {
    return this._locator;
  }

  public get updatedTimestamp(): number {
    return this._updatedTimestamp;
  }

  public get totalItems(): number {

    return this._currentDocument ? this._currentDocument.totalItems : null;
  }

  public get resfreshInterval(): number {
    return this._resfreshInterval;
  }

  public get availableItems(): number {
    return this._currentDocument ? this._currentDocument.availableItems : 0;
  }

  public get autorefresh(): boolean {
    return this._autorefresh;
  }

  public set autorefresh(enable: boolean) {
    this._autorefresh = enable;
  }

  public get metadata(): Metadata {
    return this._metadata;
  }

  protected checkExpectedtemsFromTotalItems() {
    if (this._currentDocument.totalItems && this._expectedItems > this._currentDocument.totalItems) {
      this._expectedItems = this._currentDocument.totalItems;
    }
  }

  public refresh(): void {
    this.log(`Received refresh request for view «${this.locator.getId()}»`);
    this.manualRefreshStream.next(true);
  }

  public incrementExpectedItemNumber(qty: number): void {
    if (typeof this._expectedItems === 'number') {
      this.setExpectedItemNumber(this._expectedItems + qty);
    }
  }

  public setExpectedItemNumber(qty: number): void {

    if (typeof this._expectedItems === 'number') {
      this._expectedItems = qty;
      this.fixExpectedItemNumber();

      if (!this.allExpectedItemsAreAvailable()) {
        this.refresh();
      }
    }
  }

  /**
   * Checks and modify expected value as needed.
   *
   * If expected is equal or greater than total, set expected to 'ALL'.
   *
   */
  protected fixExpectedItemNumber() {
    if (this.totalItems !== null && this._expectedItems >= this.totalItems) {
      this._expectedItems = 'ALL';
    }
  }

  public getDataAvailableStream() {
    return this.dataAvailableStream.asObservable();
  }

  public getDataFromDb(): Observable<DataContainer<T>> {
    return this._db.get(this._locator);
  }

  public documentIsFresh(): boolean {
    return this._currentDocument && !this._currentDocument.expired && !this._currentDocument.erroneous;;
  }

  public pageInfo(): RequestedItems {
    return this._expectedItems;
  }

  public mapData(dataContainer: DataContainer<any>): DataContainer<T> {

    this._metadata = new Metadata(dataContainer.totalItems);

    this.preMapItemsDataContainerProcess(dataContainer);

    let payloadToProcess = dataContainer.getPayload();
    if (payloadToProcess) {
      let processedItems = payloadToProcess.map(rawItem => this.mapItem(rawItem));
      dataContainer.setPayload(processedItems);
    }

    this.postMapItemsDataContainerProcess(dataContainer);

    return dataContainer;
  }

  public abstract updateData(): Observable<ResponseData>;

  public abstract mapItem(rawItem: any): T;

  public preMapItemsDataContainerProcess(dataContainer: DataContainer<any>): void { };

  public postMapItemsDataContainerProcess(dataContainer: DataContainer<T>): void { };

  public addDependency(dependency: ViewDataInterface) {
    this.dependentViewData.put(dependency.locator.getId(), dependency);
  }

  public connectDependencies() {
    let dependentStreams = this.dependentViewData.list();
    if (dependentStreams.length) {
      let dependentStreamsStream = this.dependentViewData.list().map(dependentViewData => dependentViewData.getDataAvailableStream());
      Observable.combineLatest(dependentStreamsStream).subscribe(v => this.dependentViewDataAvailable.next(true));
    }
    else {
      // There's no dependencies, go on.
      this.dependentViewDataAvailable.next(true);
    }
  }
}

